<script lang="ts" setup>
  import {
    XIcon,
    ThumbsDownIcon,
    ThumbsUpIcon,
    PanelRightOpenIcon,
    PanelRightCloseIcon,
    FileIcon,
    FileTextIcon,
    UsersIcon,
    PlusIcon
  } from 'lucide-vue-next'
  import { inject, ref, watch, nextTick, computed, onMounted, type Ref } from 'vue'
  import type {
    AIChatSession,
    CustomerCase,
    CustomerOrganisationUser,
    CustomerUser
  } from '@/types/generated'
  import { SessionStateEnum } from '@/types/generated'
  import AiChatMessage from '@/components/app/cases/chat/AiChatMessage.vue'
  import AiChatMessageInput from '@/components/app/cases/chat/AiChatMessageInput.vue'
  import SkeletonLoader from '@/components/skeleton/SkeletonLoader.vue'
  import DocumentUpload from '@/components/app/cases/documents/DocumentUpload.vue'
  import OffCanvas from '@/components/ui/OffCanvas.vue'
  import UserAvatar from '@/components/app/users/UserAvatar.vue'
  import { useTranslation } from '@/composables/useTranslation'

  const { t } = useTranslation({
    en: {
      ChatDescription: 'No messages',
      Document: 'Document',
      Documents: 'Documents',
      Participant: 'Party',
      Participants: 'Parties',
      Ready: 'Ready',
      UpdatingContext: 'Updating context',
      SearchingDocuments: 'Searching documents',
      Thinking: 'Thinking',
      Hello: 'Hello',
      HowCanIHelpYou: 'How can I help you?',
      ChooseQuestionTemplate:
        'Choose one of the following question templates or ask your own question.',
      Page: 'Page',
      Pages: 'Pages'
    },
    de: {
      ChatDescription: 'Ohne Nachrichten',
      Document: 'Dokument',
      Documents: 'Dokumente',
      Participant: 'Beteiligter',
      Participants: 'Beteiligte',
      Ready: 'Bereit',
      UpdatingContext: 'Aktualisiere Kontext',
      SearchingDocuments: 'Durchsuche Dokumente',
      Thinking: 'Denke nach',
      Hello: 'Hallo',
      HowCanIHelpYou: 'wie kann ich Ihnen helfen?',
      ChooseQuestionTemplate:
        'Wählen Sie eine der folgenden Fragevorlagen oder stellen Sie Ihre eigene Frage.',
      Page: 'Seite',
      Pages: 'Seiten'
    }
  })

  const activeChatSession = inject<Ref<AIChatSession | null>>('activeChatSession', ref(null))
  const isAnySessionGenerating = inject(
    'isAnySessionGenerating',
    computed(() => false)
  )
  const isSessionGenerating = inject<(uuid: string) => boolean>('isSessionGenerating', () => false)
  const isActiveSessionGenerating = computed(() => {
    return activeChatSession.value && isSessionGenerating(activeChatSession.value.uuid)
  })
  const activeChatSessionUser = computed<CustomerOrganisationUser | null>(() => {
    return (
      caseData.value?.org.users.find(
        (user) => user.uuid === activeChatSession.value?.started_by_user
      ) || null
    )
  })
  const currentUser = inject<Ref<CustomerUser | null>>('currentUser', ref(null))
  const messagesContainer = ref<HTMLElement | null>(null)
  const messages = computed(() => activeChatSession.value?.messages)
  const sendChatMessage = inject(
    'sendChatMessage',
    async (session: AIChatSession, message: string) => {}
  )
  const isDocumentPanelOpen = inject<Ref<boolean>>('isDocumentPanelOpen', ref(false))
  const toggleDocumentPanel = inject<() => void>('toggleDocumentPanel')
  const showSessionStateProgress = computed(
    () => activeChatSession.value?.session_state !== SessionStateEnum.READY
  )
  const caseData = inject<Ref<CustomerCase | null>>('caseData', ref(null))

  const stateMessage = computed(() => {
    const stateMessages: Record<SessionStateEnum, string> = {
      [SessionStateEnum.READY]: t('Ready'),
      [SessionStateEnum.UPDATING_CONTEXT]: t('UpdatingContext'),
      [SessionStateEnum.SEARCHING_DOCUMENTS]: t('SearchingDocuments'),
      [SessionStateEnum.REASONING]: t('Thinking')
    }
    const currentState = activeChatSession.value?.session_state as SessionStateEnum
    return currentState ? stateMessages[currentState] : ''
  })

  const lastUserMessage = computed(() => {
    if (!activeChatSession.value?.messages) return ''
    const userMessages = activeChatSession.value.messages
      .filter((msg) => msg.role === 'user')
      .sort((a, b) => new Date(b.created_at || '') - new Date(a.created_at || ''))
    return userMessages[0]?.content || ''
  })

  const truncatedLastMessage = computed(() => {
    const msg = lastUserMessage.value
    return msg.length > 50 ? msg.slice(0, 50) + '...' : msg
  })

  const scrollToBottom = async () => {
    await nextTick()
    if (messagesContainer.value) {
      console.log('scrolling to bottom')
      messagesContainer.value.scrollTop = messagesContainer.value.scrollHeight
    }
  }

  watch(
    () => messages.value,
    () => {
      console.log('messages changed')
      scrollToBottom()
    },
    { deep: true }
  )

  onMounted(() => {
    scrollToBottom()
  })

  const onUploadedFileAiReady = () => {
    console.log('file ai ready')
  }
</script>

<style>
  .slide-enter-active,
  .slide-leave-active {
    transition: transform 0.3s ease;
  }

  .slide-enter-from,
  .slide-leave-to {
    transform: translateX(100%);
  }

  .slide-enter-to,
  .slide-leave-from {
    transform: translateX(0);
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.3s ease;
  }

  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
  }

  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
  }
</style>

<template>
  <div class="flex h-full flex-1 flex-col">
    <div class="flex h-full flex-col">
      <div class="relative flex h-full flex-col overflow-hidden">
        <div class="flex h-full flex-col overflow-hidden">
          <header
            v-if="activeChatSession"
            class="sticky top-0 z-[9] flex items-center justify-between gap-x-2 rounded-b-none rounded-r-lg border-b
              border-gray-200 bg-white p-2 dark:border-neutral-700 dark:bg-neutral-800 sm:px-5"
          >
            <!-- User Info -->
            <div>
              <button
                type="button"
                class="flex items-center gap-x-3.5 truncate focus:outline-none"
              >
                <UserAvatar
                  :user="activeChatSessionUser"
                  size="32px"
                  v-if="activeChatSessionUser"
                />
                <span class="grow truncate text-center lg:text-start">
                  <span
                    class="block truncate text-sm font-semibold leading-4 text-gray-800 dark:text-neutral-200"
                  >
                    {{ activeChatSessionUser?.first_name }} {{ activeChatSessionUser?.last_name }}
                  </span>
                  <span
                    class="block truncate text-xs leading-4 text-gray-500 dark:text-neutral-500"
                  >
                    {{ truncatedLastMessage || t('ChatDescription') }}
                  </span>
                </span>
              </button>
            </div>

            <div class="flex items-center gap-3 text-gray-500 dark:text-neutral-400">
              <span class="text-xs">Context:</span>
              <!-- Documents -->
              <button
                class="flex items-center gap-1.5 text-xs hover:text-gray-700 dark:hover:text-neutral-300"
              >
                <FileTextIcon class="h-3.5 w-3.5" />
                <span>
                  {{
                    caseData?.documents?.length === 1
                      ? `1 ${t('Document')}`
                      : `${caseData?.documents?.length || 0} ${t('Documents')}`
                  }}
                </span>
              </button>

              <!-- Participants -->
              <button
                class="flex items-center gap-1.5 text-xs hover:text-gray-700 dark:hover:text-neutral-300"
              >
                <UsersIcon class="h-3.5 w-3.5" />
                <span>
                  {{
                    caseData?.data?.entities?.length === 1
                      ? `1 ${t('Participant')}`
                      : `${caseData?.data?.entities?.length || 0} ${t('Participants')}`
                  }}
                </span>
              </button>
            </div>
          </header>

          <div class="flex flex-1 overflow-hidden">
            <div class="flex flex-1 flex-col overflow-hidden transition-all duration-300">
              <div
                ref="messagesContainer"
                class="flex-1 overflow-y-auto"
              >
                <div class="space-y-5 p-6">
                  <div
                    v-if="!messages?.length"
                    class="flex flex-col items-center justify-center px-4"
                  >
                    <h2 class="pt-6 text-center text-3xl font-semibold tracking-tight sm:text-4xl">
                      👋
                      <div class="mt-4">
                        {{ t('Hello') }} {{ currentUser?.first_name }} {{ currentUser?.last_name }},
                        <br />
                        <span
                          class="bg-gradient-to-r from-pink-600 via-red-500 to-purple-600 bg-clip-text text-transparent"
                        >
                          {{ t('HowCanIHelpYou') }}
                        </span>
                      </div>
                    </h2>
                    <p class="mt-4 text-gray-600 dark:text-gray-400">
                      {{ t('ChooseQuestionTemplate') }}
                    </p>
                  </div>
                  <div
                    v-if="!messages?.length"
                    class="mt-8 flex flex-wrap items-center justify-center gap-2"
                  >
                    <button
                      v-for="template in caseData?.org?.ai_chat_question_templates"
                      :key="template.uuid"
                      class="mr-2 mt-2 inline-flex items-center gap-x-1.5 rounded-full border border-purple-100 bg-purple-50/50
                        px-3 py-1.5 text-sm transition-colors hover:border-purple-200 hover:bg-purple-50
                        dark:border-purple-900/30 dark:bg-purple-900/10 dark:hover:border-purple-900/50
                        dark:hover:bg-purple-900/20"
                      @click="sendChatMessage(activeChatSession, template.prompt)"
                    >
                      <span
                        v-if="template.law_field"
                        class="font-medium text-purple-900 dark:text-purple-100"
                      >
                        {{ template.law_field }}:
                      </span>
                      <span
                        :class="[
                          template.law_field ? 'text-purple-700' : 'font-medium text-purple-900',
                          'dark:text-purple-300'
                        ]"
                      >
                        {{ template.title }}
                      </span>
                    </button>
                  </div>
                  <AiChatMessage
                    :message="message"
                    v-for="message in messages"
                    :key="message.uuid"
                  />
                  <div
                    v-if="isActiveSessionGenerating"
                    class="flex gap-x-2 sm:gap-x-4"
                  >
                    <div class="w-full max-w-[90%] grow space-y-3 md:max-w-2xl">
                      <div
                        class="space-y-3 rounded-lg border border-gray-200 bg-white p-4 dark:border-neutral-700 dark:bg-neutral-900"
                      >
                        <div class="text-sm text-gray-800 dark:text-white">
                          <div class="space-y-4">
                            <div
                              v-if="showSessionStateProgress"
                              class="flex items-center gap-2 text-xs text-gray-500"
                            >
                              <div class="h-1.5 w-1.5 animate-pulse rounded-full bg-blue-500"></div>
                              {{ stateMessage }}
                            </div>
                            <div class="space-y-2">
                              <SkeletonLoader class="h-4 w-3/4" />
                              <SkeletonLoader class="h-4 w-1/2" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <AiChatMessageInput />
            </div>
          </div>
        </div>
      </div>
    </div>
    <OffCanvas
      v-model="isDocumentPanelOpen"
      position="right"
      width="w-80"
    >
      <template #title>
        {{ caseData?.documents?.length === 1 ? t('Document') : t('Documents') }}
      </template>

      <div
        v-for="document in caseData?.documents"
        :key="document.uuid"
        class="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-4 py-2 dark:border-neutral-700
          dark:bg-neutral-800"
      >
        <div
          class="relative h-10 w-10 shrink-0 overflow-hidden rounded-md border border-gray-200
            dark:border-neutral-700"
        >
          <img
            v-if="document.images?.length"
            :src="document.images.find((img) => img.image_preset === 'thumbnail')?.url"
            :alt="document.title"
            class="h-full w-full object-cover"
          />
          <div
            v-else
            class="flex h-full w-full items-center justify-center bg-gray-100 text-gray-400 dark:bg-neutral-700
              dark:text-neutral-500"
          >
            <FileIcon class="h-5 w-5" />
          </div>
        </div>

        <div class="min-w-0 flex-1">
          <h4 class="truncate text-sm font-medium text-gray-900 dark:text-white">
            {{ document.title }}
          </h4>
          <div class="mt-0.5 flex items-center gap-2 text-xs text-gray-500 dark:text-neutral-400">
            <span>
              {{
                new Date(document.created_at).toLocaleDateString('de-DE', {
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric'
                })
              }}
            </span>
            <span>•</span>
            <span>
              {{
                document.page_count === 1
                  ? `1 ${t('Page')}`
                  : `${document.page_count} ${t('Pages')}`
              }}
            </span>
          </div>
        </div>
      </div>
      <DocumentUpload
        :case-data="caseData"
        :auto-remove-after-ai-ready="true"
        @file-ai-ready="onUploadedFileAiReady"
      />
    </OffCanvas>
  </div>
</template>
